import { Grid, Link, Tooltip, Typography, useTheme, Box } from '@mui/material';
import { DividerTitle } from 'components/common/DividerTitle';
import { jbColors } from 'theme/jbColors';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { openInNewTab } from 'utils/browserUtils';

interface DynamicGridItemProps {
  item: Record<string, unknown> | string | number | null | undefined;
  attribute: string;
  index: number;
  length: number;
  title: string | undefined;
  'data-testid'?: string;
}

const checkIsObject = (attributes: unknown): attributes is Record<string, unknown> => {
  return typeof attributes === 'object';
};

const DynamicGridItem = (props: DynamicGridItemProps) => {
  const { item, attribute, index, title, 'data-testid': dataTestId } = props;
  const isObject = checkIsObject(item);
  const itemKeys = item ? Object.keys(item) : [];
  const indexOfLastItem = itemKeys.length - 1;
  const theme = useTheme();
  const selectedServiceAgreement = useSelector((state: RootState) => state.serviceAgreements.selectedServiceAgreement);
  const CLM_URL = useSelector((state: RootState) => state.appConfig.CLM_URL);

  const containerStyle = () => {
    return isObject
      ? {
          display: 'block',
          alignItems: 'center',
          marginBottom: '0.5em',
          marginTop: '0.5em',
        }
      : {
          display: 'flex',
          minHeight: '24px',
          marginBottom: '0.1em',
          alignItems: 'center',
          borderBottom: `1px solid ${jbColors.core.stone[30]}`,
          borderTop: index === 0 ? `1px solid ${jbColors.core.stone[30]}` : 'none',
        };
  };

  return (
    <Grid data-testid="dynamic-grid-item" container item xs={12} key={index} sx={containerStyle()}>
      {isObject ? (
        <div
          data-testid="dynamic-grid-item-title"
          style={{
            marginBottom: isObject ? '0.1em' : 0,
          }}
        >
          <DividerTitle text={title ? title : attribute} />
        </div>
      ) : (
        <Grid
          item
          xs={5}
          sx={{
            display: 'flex',
            backgroundColor: jbColors.core.stone[10],
            height: '100%',
            alignItems: 'center',
          }}
        >
          <Typography sx={{ paddingLeft: theme.spacing(1) }} data-testid="dynamic-grid-item-attribute">
            {' '}
            {attribute}{' '}
          </Typography>
        </Grid>
      )}
      {isObject ? (
        itemKeys?.map((key) => (
          <Grid
            container
            item
            xs={12}
            key={key}
            data-testid="dynamic-grid-item-key-value-pair"
            sx={{
              display: 'flex',
              alignItems: 'center',
              minHeight: '24px',
              marginBottom: 0,
              borderBottom:
                itemKeys.indexOf(key) < indexOfLastItem && itemKeys.length > 0
                  ? `1px solid ${jbColors.core.stone[30]}`
                  : 'none',
            }}
          >
            <Grid item data-testid="dynamic-grid-item-key-container" sx={{}}>
              <Typography
                data-testid="dynamic-grid-item-key-text"
                title={key}
                sx={{
                  backgroundColor: jbColors.core.stone[10],
                  height: '100%',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  width: '15rem',
                  p: '0.75em',
                }}
              >
                {key}
              </Typography>
            </Grid>
            <Typography
              sx={{ paddingLeft: theme.spacing(1) }}
              data-testid="dynamic-grid-item-value-text"
              title={item[key]?.toString()}
            >
              {' '}
              {item[key]?.toString()}{' '}
            </Typography>
          </Grid>
        ))
      ) : attribute === 'IP Address' ? (
        <Grid
          item
          onClick={() => openInNewTab(selectedServiceAgreement?.serviceRoles?.[0]?.partnerUid, undefined, CLM_URL)}
          sx={{ display: 'flex', alignContent: 'center' }}
        >
          <Typography sx={{ p: theme.spacing(1), mr: 1 }}> {item?.toString()} </Typography>
          {selectedServiceAgreement?.serviceRoles?.[0]?.partnerUid && (
            <Tooltip
              sx={{
                cursor: 'pointer',
                borderRadius: '50%',
                '&:hover': {
                  backgroundColor: jbColors.core.reflexBlue[100],
                  color: 'white',
                },
              }}
              arrow
              title={
                <Box
                  sx={{
                    backgroundColor: 'black',
                    display: 'flex',
                    flexDirection: 'column',
                    mt: '-0.4em',
                    mb: '-0.4em',
                    ml: '-0.8em',
                    mr: '-0.8em',
                    p: '0.4em',
                  }}
                >
                  <Typography sx={{ color: 'white' }}>Data not available here</Typography>
                  <Typography sx={{ mt: 2, color: 'white' }} variant="body2">
                    This data can be viewed and edited in the partner profile. Please make any required changes there.
                  </Typography>
                  <Typography sx={{ mt: 2, color: 'white' }} variant="body2">
                    For instructions on changes click{' '}
                    <Link
                      data-testid="dynamic-grid-item-link"
                      sx={{
                        color: 'white',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        window
                          ?.open(
                            'https://sites.juliusbaer.com/d/KotK1XLmdo9r/guidelines#/change-ip-addresses-and-or-ssh-keys/overview',
                            '_blank',
                          )
                          ?.focus();
                      }}
                    >
                      here
                    </Link>
                    .
                  </Typography>
                </Box>
              }
            >
              <InfoOutlinedIcon />
            </Tooltip>
          )}
        </Grid>
      ) : (
        <Grid item data-testid={dataTestId}>
          <Typography sx={{ paddingLeft: theme.spacing(1) }}> {item?.toString()} </Typography>
        </Grid>
      )}
    </Grid>
  );
};

export default DynamicGridItem;
